import React, { useState, useEffect, useRef } from 'react';
import { HexColorPicker } from 'react-colorful';
import { colord, extend } from 'colord';
import namesPlugin from 'colord/plugins/names';

import { ColorPickerRow, BackgroundPreview, Block, Input } from './styles';
import { CreatorSection, SectionHeader, Text, ErrorText } from '../styles';

extend([namesPlugin]);

const defaultLightMode = '#EDF3F7';

// Checks if a color starts with # sign
const isHexColor = (color) => /^#/.test(color);

// Check if hex color has 3 or 6 color values and no alpha
const isValidHexColor = (color) => /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);

// Check if a color is valid to use as background color
const isValidColor = (color) => colord(color).parsed && (!isHexColor(color) || isValidHexColor(color));

export default React.memo(({ background, setBackground }) => {
  const [textLightMode, setTextLightMode] = useState(defaultLightMode);
  const [lightBackground, setLightBackground] = useState(defaultLightMode);
  const [errorLight, setErrorLight] = useState('');

  const [textDarkMode, setTextDarkMode] = useState('');
  const [darkModeBackground, setDarkModeBackground] = useState('');
  const [errorDark, setErrorDark] = useState('');

  const tmpBackground = useRef(undefined);

  // wrapper around setBackground to always set tmpBackground to avoid circular behaviour in useEffect
  const updateBackground = (newBackground) => {
    tmpBackground.current = newBackground;
    setBackground(newBackground);
  };

  useEffect(() => {
    if (!background) return;
    if (background === tmpBackground.current) return;
    const parts = background.split('|', 2);
    if (!parts.length) return;

    setTextLightMode(parts[0]);
    if (isValidColor(parts[0])) setLightBackground(colord(parts[0]).toHex());
    else setErrorLight(`"${parts[0]}" is not valid`);

    if (parts.length < 2) return;

    setTextDarkMode(parts[1]);
    if (isValidColor(parts[1])) setDarkModeBackground(colord(parts[1]).toHex());
    else setErrorDark(`"${parts[1]}" is not valid`);
  }, [background]);

  const onTextChangeLightMode = (colorText) => {
    setTextLightMode(colorText);

    if (!colorText || !isValidColor(colorText)) return setErrorLight(`"${colorText}" is not valid`);
    else if (errorLight) setErrorLight(undefined);

    if (colorText === lightBackground) return;

    setLightBackground(colord(colorText).toHex());
    const parts = background.split('|', 2);
    if (parts.length > 1) return updateBackground(`${colorText}|${parts[1]}`);
    updateBackground(colorText);
  };

  const onTextChangeDarkMode = (colorText) => {
    setTextDarkMode(colorText);

    if (!colorText) {
      if (errorDark) setErrorDark(undefined);
      setDarkModeBackground('');
      const parts = background.split('|', 2);
      return updateBackground(parts[0]);
    }

    if (!isValidColor(colorText)) return setErrorDark(`"${colorText}" is not valid`);
    else if (errorDark) setErrorDark(undefined);

    if (colorText === darkModeBackground) return;

    setDarkModeBackground(colord(colorText).toHex());
    const parts = background.split('|', 2);
    updateBackground(`${parts[0]}|${colorText}`);
  };

  const onChangeLightMode = (color) => {
    setLightBackground(color);
    setTextLightMode(color);
    if (errorLight) setErrorLight(undefined);
    const parts = background.split('|', 2);
    if (parts.length > 1) return updateBackground(`${color}|${parts[1]}`);
    updateBackground(color);
  };

  const onChangeDarkMode = (color) => {
    setDarkModeBackground(color);
    setTextDarkMode(color);
    if (errorDark) setErrorDark(undefined);
    const parts = background.split('|', 2);
    updateBackground(`${parts[0]}|${color}`);
  };

  return (
    <CreatorSection>
      <SectionHeader>Background Color</SectionHeader>
      <Text>Canvas background color where not covered by graphical elements</Text>
      <ColorPickerRow>
        <Block>
          <HexColorPicker color={lightBackground} onChange={onChangeLightMode} />
        </Block>
        <Block>
          <Input
            value={textLightMode}
            placeholder="Light mode color"
            onChange={(e) => onTextChangeLightMode(e.target.value)}
          />
          <BackgroundPreview backgroundColor={lightBackground}></BackgroundPreview>
        </Block>
      </ColorPickerRow>
      {errorLight && <ErrorText>{errorLight}</ErrorText>}
      <ColorPickerRow>
        <Block>
          <HexColorPicker color={darkModeBackground || lightBackground} onChange={onChangeDarkMode} />
        </Block>
        <Block>
          <Input
            value={textDarkMode}
            placeholder="Dark mode color"
            onChange={(e) => onTextChangeDarkMode(e.target.value)}
          />
          <BackgroundPreview backgroundColor={darkModeBackground || lightBackground}></BackgroundPreview>
        </Block>
      </ColorPickerRow>
      {errorDark && <ErrorText>{errorDark}</ErrorText>}
    </CreatorSection>
  );
});
